import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import { useTheme } from "../../context/ThemeContext";
import { Dialog, DialogContent } from "../../components/ui/dialog";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import { Badge } from "../../components/ui/badge";
import {
  Tag,
  FileText,
  PlusCircle,
  X,
  Trash2,
  CheckSquare,
  List,
  Image as ImageIcon,
  GripVertical,
  Languages,
  Globe,
  LayoutGrid,
  Save,
  ChevronLeft,
  Upload,
  Sparkles,
  Loader2,
  Calendar,
  Scale,
  ListPlus,
  Video,
  Play,
  Link2,
  AlertCircle,
} from "lucide-react";
import {
  Dialog as NestedDialog,
  DialogContent as NestedDialogContent,
  DialogHeader as NestedDialogHeader,
  DialogTitle as NestedDialogTitle,
  DialogDescription as NestedDialogDescription,
  DialogFooter as NestedDialogFooter,
} from "../../components/ui/dialog";

const FIELD_TYPES = {
  TEXT: "text",
  TEXTAREA: "textarea",
  IMAGE: "image",
  VIDEO: "video",
  CHECKBOX: "checkbox",
  SELECT: "select",
  NUMBER: "number",
  DATE: "date",
  UNIT: "unit",
  ARRAY: "array",
  DOCUMENT: "document",
};

// Turn a stored document key into a human-friendly filename by removing the
// appended "__<uuid>" we add for uniqueness. Falls back to a generic label for
// legacy keys whose whole name is just a uuid.
const UUID_RE =
  "[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}";
const prettyDocumentName = (filename) => {
  if (typeof filename !== "string" || !filename) return "Document.pdf";
  let name = filename.split("/").pop() || filename;
  name = name.replace(new RegExp(`__${UUID_RE}(?=\\.[^.]+$|$)`, "i"), "");
  if (new RegExp(`^${UUID_RE}(\\.[^.]+)?$`, "i").test(name)) return "Document.pdf";
  return name.toLowerCase().endsWith(".pdf") ? name : `${name}.pdf`;
};

const VIDEO_EXTENSIONS = [".mp4", ".webm", ".ogg", ".mov", ".avi", ".mkv"];
const isVideoFile = (filename) => {
  if (!filename || typeof filename !== "string") return false;
  const lower = filename.toLowerCase();
  return VIDEO_EXTENSIONS.some((ext) => lower.endsWith(ext));
};

const S3_BASE_URL = process.env.REACT_APP_S3_BASE_URL;

const ItemEditor = ({
  open,
  onOpenChange,
  editedItems,
  isAddingNewItem,
  onSave,
  onCancel,
  onDeleteField,
  onAddField,
  onEditField,
  onReorderFields,
  onConvertSelectToCategory,
  onDeleteImage,
  existingCategories = [],
  allItems = {},
  uploadProgress = null,
  uploadError = null,
  onDismissUploadError = () => {},
}) => {
  const { isAdmin } = useAuth();
  const { theme } = useTheme();
  const isDark = theme === "dark";
  const navigate = useNavigate();
  const [newFieldName, setNewFieldName] = useState("");
  const [newFieldType, setNewFieldType] = useState(FIELD_TYPES.TEXT);
  const [selectOptions, setSelectOptions] = useState([""]);
  const [selectSource, setSelectSource] = useState("manual"); // 'manual' | 'category'
  const [selectedCategory, setSelectedCategory] = useState("");
  const [newFieldUnit, setNewFieldUnit] = useState("");
  const [fieldToDelete, setFieldToDelete] = useState(null);
  // Manual-select -> category-link conversion
  const [linkSelectField, setLinkSelectField] = useState(null);
  const [linkSelectCategory, setLinkSelectCategory] = useState("");
  const [linkSelectDialogOpen, setLinkSelectDialogOpen] = useState(false);
  const [addFieldDialogOpen, setAddFieldDialogOpen] = useState(false);
  const [deleteFieldDialogOpen, setDeleteFieldDialogOpen] = useState(false);
  // Translation states
  const [useTranslations, setUseTranslations] = useState(false);
  const [availableLanguages, setAvailableLanguages] = useState([
    "nl",
    "fr",
    "en",
  ]);
  const [currentLanguage, setCurrentLanguage] = useState("nl");
  const [addLanguageDialogOpen, setAddLanguageDialogOpen] = useState(false);
  const [newLanguageCode, setNewLanguageCode] = useState("");
  const [autoTranslateDialogOpen, setAutoTranslateDialogOpen] = useState(false);
  const [isTranslating, setIsTranslating] = useState(false);
  const [selectedFieldsForTranslation, setSelectedFieldsForTranslation] =
    useState([]);
  const [dragState, setDragState] = useState({
    field: null,
    fromIdx: null,
    overIdx: null,
  });

  // Helper to get display value from potentially translated field
  const getDisplayValue = (value, preferredLang = "nl") => {
    if (typeof value === "object" && value !== null && !Array.isArray(value)) {
      return value[preferredLang] || value.nl || value.fr || value.en || "";
    }
    return value || "";
  };

  // Initialize translation state
  useEffect(() => {
    if (editedItems?.item?._translations) {
      setUseTranslations(true);
      const langs = editedItems.item._translations.languages || [
        "nl",
        "fr",
        "en",
      ];
      setAvailableLanguages(langs);
      if (!langs.includes(currentLanguage)) {
        setCurrentLanguage(langs[0] || "nl");
      }
    } else {
      setUseTranslations(false);
      setAvailableLanguages(["nl", "fr", "en"]);
      setCurrentLanguage("nl");
    }
  }, [editedItems]);

  // --- Functions ---
  function handleDeleteField(field) {
    onDeleteField(field);
    setDeleteFieldDialogOpen(false);
    setFieldToDelete(null);
  }

  const handleToggleTranslations = (enabled) => {
    setUseTranslations(enabled);
    if (enabled) {
      const translations = { enabled: true, languages: ["nl", "fr", "en"] };
      Object.keys(editedItems.item).forEach((field) => {
        if (shouldFieldBeTranslated(field, editedItems.item[field])) {
          const currentValue = editedItems.item[field];
          if (Array.isArray(currentValue)) {
            const newArray = currentValue.map((item) =>
              typeof item === "string"
                ? {
                    nl: item,
                    fr: "",
                    en: "",
                  }
                : item,
            );
            onEditField(field, newArray);
          } else if (
            typeof currentValue !== "object" ||
            currentValue === null
          ) {
            onEditField(field, { nl: currentValue || "", fr: "", en: "" });
          }
        }
      });
      onEditField("_translations", translations);
      setAvailableLanguages(["nl", "fr", "en"]);
      setCurrentLanguage("nl");
    } else {
      Object.keys(editedItems.item).forEach((field) => {
        if (
          field !== "_translations" &&
          field !== "id" &&
          editedItems.item[field] !== null
        ) {
          const value = editedItems.item[field];
          if (Array.isArray(value)) {
            const newArray = value.map((item) =>
              typeof item === "object"
                ? item.nl || item.fr || item.en || ""
                : item,
            );
            onEditField(field, newArray);
          } else if (typeof value === "object") {
            if (
              value.nl !== undefined ||
              value.fr !== undefined ||
              value.en !== undefined
            ) {
              onEditField(field, value.nl || value.fr || value.en || "");
            }
          }
        }
      });
      onEditField("_translations", undefined);
    }
  };

  const shouldFieldBeTranslated = (fieldName, value) => {
    if (["id", "name", "_fieldMetadata", "_translations"].includes(fieldName)) {
      return fieldName === "name";
    }
    const metadata = getFieldMetadata(fieldName);
    return (
      metadata.type === FIELD_TYPES.TEXT ||
      metadata.type === FIELD_TYPES.TEXTAREA ||
      metadata.type === FIELD_TYPES.ARRAY
    );
  };

  const getTranslatedValue = (field, value) => {
    if (!useTranslations) return value;
    if (typeof value === "object" && value !== null && !Array.isArray(value)) {
      return value[currentLanguage] || "";
    }
    return value;
  };

  const setTranslatedValue = (field, newValue) => {
    if (!useTranslations) {
      onEditField(field, newValue);
      return;
    }
    const currentFieldValue = editedItems.item[field];
    let translationObj =
      typeof currentFieldValue === "object" &&
      currentFieldValue !== null &&
      !Array.isArray(currentFieldValue)
        ? { ...currentFieldValue }
        : availableLanguages.reduce(
            (acc, lang) => ({ ...acc, [lang]: "" }),
            {},
          );

    translationObj[currentLanguage] = newValue;
    onEditField(field, translationObj);
  };

  const handleAddLanguage = () => {
    const langCode = newLanguageCode.trim().toLowerCase();
    if (!langCode || availableLanguages.includes(langCode)) return;
    const newLanguages = [...availableLanguages, langCode];
    setAvailableLanguages(newLanguages);
    const translations = editedItems.item._translations || {
      enabled: true,
      languages: newLanguages,
    };
    translations.languages = newLanguages;
    onEditField("_translations", translations);
    setNewLanguageCode("");
    setAddLanguageDialogOpen(false);
  };

  // --- Auto Translate Logic ---
  const translateText = async (text, sourceLang, targetLang) => {
    try {
      const url = `https://translate.googleapis.com/translate_a/single?client=gtx&sl=${sourceLang}&tl=${targetLang}&dt=t&q=${encodeURIComponent(text)}`;
      const response = await fetch(url);
      const data = await response.json();
      if (data && Array.isArray(data[0])) {
        return data[0].map((seg) => (seg && seg[0] ? seg[0] : "")).join("");
      }
      return text;
    } catch (error) {
      console.error("Translation API error:", error);
      return text;
    }
  };

  const getTranslatableFields = () => {
    const fields = [];
    Object.keys(editedItems.item).forEach((field) => {
      if (shouldFieldBeTranslated(field, editedItems.item[field])) {
        const value = editedItems.item[field];
        // Only if it has content in current language
        if (Array.isArray(value)) {
          // For arrays, check if any item has content
          const hasContent = value.some(
            (item) =>
              typeof item === "object" &&
              item !== null &&
              item[currentLanguage],
          );
          if (hasContent) {
            fields.push({
              field,
              displayName: getFieldDisplayName(field),
              preview: `Lijst met ${value.length} items`,
            });
          }
        } else if (
          typeof value === "object" &&
          value !== null &&
          value[currentLanguage]
        ) {
          fields.push({
            field,
            displayName: getFieldDisplayName(field),
            preview: value[currentLanguage].substring(0, 30) + "...",
          });
        }
      }
    });
    return fields;
  };

  const openAutoTranslateDialog = () => {
    const fields = getTranslatableFields();
    setSelectedFieldsForTranslation(fields.map((f) => f.field));
    setAutoTranslateDialogOpen(true);
  };

  const toggleFieldSelection = (field) => {
    setSelectedFieldsForTranslation((prev) =>
      prev.includes(field) ? prev.filter((f) => f !== field) : [...prev, field],
    );
  };

  const handleAutoTranslate = async () => {
    setIsTranslating(true);
    try {
      const targetLanguages = availableLanguages.filter(
        (lang) => lang !== currentLanguage,
      );

      for (const field of selectedFieldsForTranslation) {
        const value = editedItems.item[field];
        // Handle standard translation objects
        if (Array.isArray(value)) {
          const newArray = [...value];
          let updated = false;
          for (let i = 0; i < newArray.length; i++) {
            const item = newArray[i];
            if (
              typeof item === "object" &&
              item !== null &&
              item[currentLanguage]
            ) {
              const text = item[currentLanguage];
              const newItem = { ...item };
              for (const targetLang of targetLanguages) {
                // translate if empty or overwrite? User said: "Huidige en lege vertalingen worden overschreven" (in dialog text)
                // so we overwrite.
                newItem[targetLang] = await translateText(
                  text,
                  currentLanguage,
                  targetLang,
                );
              }
              newArray[i] = newItem;
              updated = true;
            }
          }
          if (updated) onEditField(field, newArray);
        } else if (
          typeof value === "object" &&
          value !== null &&
          !Array.isArray(value)
        ) {
          const text = value[currentLanguage];
          if (!text) continue;

          const newTranslationObj = { ...value };
          for (const targetLang of targetLanguages) {
            newTranslationObj[targetLang] = await translateText(
              text,
              currentLanguage,
              targetLang,
            );
          }
          onEditField(field, newTranslationObj);
        }
      }
      setAutoTranslateDialogOpen(false);
    } catch (error) {
      console.error(error);
      alert("Fout bij vertalen");
    } finally {
      setIsTranslating(false);
    }
  };

  const getFieldMetadata = (fieldName) => {
    const value = editedItems?.item?.[fieldName];
    const fieldType = detectFieldType(fieldName, value);
    let options = [];

    if (fieldType === FIELD_TYPES.SELECT) {
      if (fieldName.includes("[") && fieldName.includes("]")) {
        const optionsMatch = fieldName.match(/\[(.*?)\]/);
        if (optionsMatch) options = optionsMatch[1].split("|");
      } else if (fieldName.endsWith("_select")) {
        // Dynamic options from category
        const categoryName = fieldName.replace("_select", "");
        if (allItems && allItems[categoryName]) {
          options = allItems[categoryName]
            .map((i) => {
              const name =
                typeof i.name === "object" && i.name !== null
                  ? i.name.nl ||
                    i.name.en ||
                    i.name.fr ||
                    Object.values(i.name)[0] ||
                    ""
                  : i.name;
              return i.id ? `${i.id}:${name}` : name;
            })
            .filter(Boolean);
        }
      }
    }

    let derivedUnit = undefined;
    if (/_unit$/.test(fieldName)) {
      const m = fieldName.match(/_(.*?)_unit$/);
      if (m && m[1]) derivedUnit = m[1];
    }
    return { type: fieldType, options: options, unit: derivedUnit };
  };

  const detectFieldType = (fieldName, value) => {
    if (/_unit$/.test(fieldName)) return FIELD_TYPES.UNIT;
    if (fieldName.includes("_array")) return FIELD_TYPES.ARRAY;
    if (
      fieldName.includes("_document") ||
      fieldName.toLowerCase().includes("document")
    )
      return FIELD_TYPES.DOCUMENT;
    if (
      fieldName.includes("_video") ||
      fieldName.toLowerCase().includes("video")
    )
      return FIELD_TYPES.VIDEO;
    if (
      fieldName.includes("_image") ||
      fieldName.toLowerCase().includes("afbeelding")
    )
      return FIELD_TYPES.IMAGE;
    if (
      fieldName.includes("_date") ||
      fieldName.toLowerCase().includes("datum")
    )
      return FIELD_TYPES.DATE;
    if (fieldName.includes("[") && fieldName.includes("]"))
      return FIELD_TYPES.SELECT;
    if (fieldName.includes("_check")) return FIELD_TYPES.CHECKBOX;
    if (fieldName.includes("_number")) return FIELD_TYPES.NUMBER;
    if (fieldName.includes("_description")) return FIELD_TYPES.TEXTAREA;
    if (fieldName.includes("_select")) return FIELD_TYPES.SELECT;
    if (
      Array.isArray(value) &&
      value.every(
        (item) =>
          typeof item === "string" ||
          (typeof item === "object" && (item.nl || item.fr || item.en)),
      )
    )
      return FIELD_TYPES.ARRAY;
    if (typeof value === "string" && /^\d{4}-\d{2}-\d{2}$/.test(value))
      return FIELD_TYPES.DATE;
    if (typeof value === "boolean") return FIELD_TYPES.CHECKBOX;
    if (typeof value === "number") return FIELD_TYPES.NUMBER;
    if (value && typeof value === "object" && value.type === "image")
      return FIELD_TYPES.IMAGE;
    if (value && typeof value === "object" && value.type === "video")
      return FIELD_TYPES.VIDEO;
    return FIELD_TYPES.TEXT;
  };

  const getGroupedFields = () => {
    if (!editedItems)
      return { basis: [], extra: [], images: [], largeText: [] };
    const basis = ["name"];
    const extra = [];
    const images = [];
    const largeText = [];

    Object.keys(editedItems.item)
      .filter(
        (key) =>
          ![
            "id",
            "name",
            "isVisible",
            "_fieldMetadata",
            "_translations",
          ].includes(key),
      )
      .forEach((key) => {
        const metadata = getFieldMetadata(key);
        // Deduplicate images
        const hasBetterVersion =
          (key === "afbeelding" && editedItems.item["afbeelding_image"]) ||
          (key === "afbeeldnig" &&
            (editedItems.item["afbeelding"] ||
              editedItems.item["afbeelding_image"]));
        if (hasBetterVersion) return;

        if (
          metadata.type === FIELD_TYPES.IMAGE ||
          metadata.type === FIELD_TYPES.VIDEO
        ) {
          images.push(key);
        } else if (metadata.type === FIELD_TYPES.TEXTAREA) {
          largeText.push(key);
        } else {
          extra.push(key);
        }
      });
    return { basis, extra, images, largeText };
  };

  const handleAddField = () => {
    if (!newFieldName.trim()) return;
    let fieldName = newFieldName.trim();
    let type = newFieldType;
    if (
      (fieldName.toLowerCase() === "afbeelding" ||
        fieldName.toLowerCase() === "image") &&
      type !== FIELD_TYPES.IMAGE
    )
      type = FIELD_TYPES.IMAGE;

    // Apply suffixes ensuring no duplication
    if (type === FIELD_TYPES.CHECKBOX && !fieldName.endsWith("_check"))
      fieldName += "_check";
    if (type === FIELD_TYPES.NUMBER && !fieldName.endsWith("_number"))
      fieldName += "_number";
    if (type === FIELD_TYPES.UNIT) {
      const unitSuffix = `_${(newFieldUnit || "UNIT").replace(/_/g, "-")}_unit`;
      if (!fieldName.endsWith(unitSuffix)) fieldName += unitSuffix;
    }
    if (type === FIELD_TYPES.IMAGE && !fieldName.endsWith("_image"))
      fieldName += "_image";
    if (type === FIELD_TYPES.VIDEO && !fieldName.endsWith("_video"))
      fieldName += "_video";
    if (type === FIELD_TYPES.TEXTAREA && !fieldName.endsWith("_description"))
      fieldName += "_description";
    if (type === FIELD_TYPES.ARRAY && !fieldName.endsWith("_array"))
      fieldName += "_array";
    if (type === FIELD_TYPES.DATE && !fieldName.endsWith("_date"))
      fieldName += "_date";
    if (type === FIELD_TYPES.DOCUMENT && !fieldName.endsWith("_document"))
      fieldName += "_document";

    if (type === FIELD_TYPES.SELECT) {
      if (selectSource === "category" && selectedCategory) {
        // For category-based selects, use dynamic options by NOT adding brackets
        // This allows getFieldMetadata to fetch current items from allItems dynamically
        fieldName = selectedCategory + "_select";
      } else {
        let validOptions = selectOptions.filter((o) => o.trim());
        fieldName += `_select[${validOptions.join("|")}]`;
      }
    }

    let defaultValue = "";
    if (type === FIELD_TYPES.CHECKBOX) defaultValue = false;
    if (type === FIELD_TYPES.NUMBER) defaultValue = 0;
    if (
      type === FIELD_TYPES.IMAGE ||
      type === FIELD_TYPES.VIDEO ||
      type === FIELD_TYPES.DOCUMENT ||
      type === FIELD_TYPES.ARRAY
    )
      defaultValue = [];
    if (type === FIELD_TYPES.SELECT) defaultValue = ""; // Default empty, user selects later
    if (
      useTranslations &&
      (type === FIELD_TYPES.TEXT || type === FIELD_TYPES.TEXTAREA)
    )
      defaultValue = { nl: "", fr: "", en: "" };

    onAddField(fieldName, defaultValue);
    setNewFieldName("");
    setAddFieldDialogOpen(false);
  };

  const getFieldDisplayName = (fieldName) => {
    let displayName = fieldName;
    displayName = displayName.replace(/\[.*?\]$/, "");
    displayName = displayName.replace(/_(.*?)_unit$/, "");
    displayName = displayName.replace(/_check$/, "");
    displayName = displayName.replace(/_number$/, "");
    displayName = displayName.replace(/_image$/, "");
    displayName = displayName.replace(/_video$/, "");
    displayName = displayName.replace(/_description$/, "");
    displayName = displayName.replace(/_select$/, "");
    displayName = displayName.replace(/_date$/, "");
    displayName = displayName.replace(/_array$/, "");
    displayName = displayName.replace(/_document$/, "");
    return displayName;
  };

  const renderFieldInput = (field, value, condensed = false) => {
    const metadata = getFieldMetadata(field);
    const isTranslatable =
      useTranslations && shouldFieldBeTranslated(field, value);
    const displayValue = isTranslatable
      ? getTranslatedValue(field, value)
      : value;
    const handleChange = isTranslatable
      ? (v) => setTranslatedValue(field, v)
      : (v) => onEditField(field, v);
    const inputClass = `h-9 text-[13px] rounded-lg ${isDark ? "bg-white/[0.04] border-white/[0.06] text-white placeholder-gray-600 focus:border-white/15" : "bg-white border-gray-200 text-gray-900 placeholder-gray-400 focus:border-gray-300"}`;

    switch (metadata.type) {
      case FIELD_TYPES.CHECKBOX:
        return (
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={() => onEditField(field, !value)}
              className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${value ? (isDark ? "bg-white" : "bg-gray-900") : isDark ? "bg-white/[0.08]" : "bg-gray-200"}`}
            >
              <span
                className={`inline-block h-3 w-3 transform rounded-full transition duration-200 ease-in-out ${value ? (isDark ? "translate-x-5 bg-black" : "translate-x-5 bg-white") : isDark ? "translate-x-1 bg-gray-500" : "translate-x-1 bg-white"}`}
              />
            </button>
            <span
              className={`text-[12px] font-medium ${isDark ? "text-gray-400" : "text-gray-600"}`}
            >
              {value ? "Actief" : "Inactief"}
            </span>
          </div>
        );
      case FIELD_TYPES.TEXTAREA:
        return (
          <div className="relative">
            <Textarea
              value={displayValue || ""}
              onChange={(e) => handleChange(e.target.value)}
              className={`min-h-[120px] resize-y text-[13px] rounded-lg ${isDark ? "bg-white/[0.04] border-white/[0.06] text-white placeholder-gray-600 focus:border-white/15" : "bg-white border-gray-200 text-gray-900 placeholder-gray-400 focus:border-gray-300"}`}
              placeholder="Typ hier..."
            />
          </div>
        );
      case FIELD_TYPES.NUMBER:
      case FIELD_TYPES.UNIT:
        return (
          <div className="relative">
            <Input
              type={metadata.type === FIELD_TYPES.NUMBER ? "number" : "text"}
              value={value || ""}
              onChange={(e) =>
                onEditField(
                  field,
                  metadata.type === FIELD_TYPES.NUMBER
                    ? parseFloat(e.target.value)
                    : e.target.value,
                )
              }
              className={`${inputClass} pr-10`}
            />
            {metadata.unit && (
              <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                <span className="text-gray-400 text-xs font-medium">
                  {metadata.unit}
                </span>
              </div>
            )}
          </div>
        );
      case FIELD_TYPES.SELECT:
        // Category-linked selects store the referenced item's id. Normalize the
        // current value to its id-part so legacy "id:label" data still matches
        // the option (whose value is the bare id) and shows as selected.
        const selectedValue =
          typeof value === "string" && value.includes(":")
            ? value.split(":")[0]
            : value || "";
        return (
          <select
            value={selectedValue}
            onChange={(e) => onEditField(field, e.target.value)}
            className={`w-full h-9 text-[13px] rounded-lg px-3 ${isDark ? "bg-white/[0.04] border border-white/[0.06] text-white" : "bg-white border border-gray-200 text-gray-900"}`}
          >
            <option value="">Selecteer...</option>
            {metadata.options.map((opt, i) => {
              const hasId = opt.includes(":");
              const optValue = hasId ? opt.split(":")[0] : opt;
              const optLabel = hasId ? opt.split(":")[1] : opt;
              return (
                <option key={i} value={optValue}>
                  {optLabel}
                </option>
              );
            })}
          </select>
        );
      case FIELD_TYPES.IMAGE:
        const imageIds = Array.isArray(value) ? value : value ? [value] : [];

        const handleDragStart = (idx) => (e) => {
          setDragState({ field, fromIdx: idx, overIdx: idx });
          e.dataTransfer.effectAllowed = "move";
        };
        const handleDragOver = (idx) => (e) => {
          e.preventDefault();
          e.dataTransfer.dropEffect = "move";
          if (dragState.field === field && dragState.overIdx !== idx) {
            setDragState((prev) => ({ ...prev, overIdx: idx }));
          }
        };
        const handleDrop = (idx) => (e) => {
          e.preventDefault();
          if (dragState.field !== field || dragState.fromIdx === null) return;
          const from = dragState.fromIdx;
          const to = idx;
          if (from !== to) {
            const reordered = [...imageIds];
            const [moved] = reordered.splice(from, 1);
            reordered.splice(to, 0, moved);
            onEditField(field, reordered, true);
          }
          setDragState({ field: null, fromIdx: null, overIdx: null });
        };
        const handleDragEnd = () => {
          setDragState({ field: null, fromIdx: null, overIdx: null });
        };

        return (
          <div className="space-y-3">
            <div className="grid grid-cols-3 sm:grid-cols-4 gap-2 sm:gap-3">
              {imageIds.map((val, idx) => {
                let src = null;
                let isUploading = false;
                let isVideo = false;

                if (val instanceof File) {
                  src = URL.createObjectURL(val);
                  isUploading = true;
                  isVideo = val.type.startsWith("video/");
                } else if (typeof val === "string") {
                  if (val.startsWith("images/") || val.startsWith("videos/"))
                    src = `${S3_BASE_URL}/${val}`;
                  else if (val.startsWith("http")) src = val;
                  isVideo = isVideoFile(val);
                }

                return (
                  <div
                    key={idx}
                    draggable
                    onDragStart={handleDragStart(idx)}
                    onDragOver={handleDragOver(idx)}
                    onDrop={handleDrop(idx)}
                    onDragEnd={handleDragEnd}
                    className={`aspect-square relative group rounded-lg overflow-hidden border transition-all cursor-grab active:cursor-grabbing ${isDark ? "bg-white/[0.02]" : "bg-gray-50"} ${
                      dragState.field === field && dragState.overIdx === idx
                        ? isDark
                          ? "border-white/30 ring-2 ring-white/10 scale-[1.02]"
                          : "border-gray-400 ring-2 ring-gray-200 scale-[1.02]"
                        : dragState.field === field && dragState.fromIdx === idx
                          ? isDark
                            ? "opacity-40 border-white/[0.06]"
                            : "opacity-40 border-gray-100"
                          : isDark
                            ? "border-white/[0.06]"
                            : "border-gray-100"
                    }`}
                  >
                    {src ? (
                      <>
                        {isVideo ? (
                          <div className="relative w-full h-full">
                            <video
                              src={src}
                              className={`w-full h-full object-cover ${isUploading ? "opacity-50" : ""}`}
                              muted
                              playsInline
                              preload="metadata"
                            />
                            <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                              <div className="bg-black/50 rounded-full p-1.5">
                                <Play className="w-4 h-4 text-white fill-white" />
                              </div>
                            </div>
                          </div>
                        ) : (
                          <img
                            src={src}
                            className={`w-full h-full object-cover ${isUploading ? "opacity-50" : ""}`}
                            alt=""
                          />
                        )}
                        {isUploading && (
                          <div className="absolute inset-0 flex items-center justify-center bg-black/10">
                            <Loader2 className="w-6 h-6 text-white animate-spin" />
                          </div>
                        )}
                      </>
                    ) : (
                      <div className="flex items-center justify-center w-full h-full">
                        <ImageIcon className="text-gray-300 w-5 h-5" />
                      </div>
                    )}
                    <button
                      onClick={() => onDeleteImage(field, val)}
                      className="absolute top-1 right-1 bg-white/90 p-1 rounded-full text-red-500 opacity-0 group-hover:opacity-100 transition-opacity z-10"
                    >
                      <X className="w-3 h-3" />
                    </button>
                    <div className="absolute top-1 left-1 bg-white/90 p-1 rounded-full text-gray-400 opacity-0 group-hover:opacity-100 transition-opacity z-10">
                      <GripVertical className="w-3 h-3" />
                    </div>
                    {idx === 0 && (
                      <div
                        className={`absolute bottom-1 left-1 text-[8px] font-bold px-1.5 py-0.5 rounded-full z-10 ${isDark ? "bg-white text-black" : "bg-gray-900 text-white"}`}
                      >
                        HOOFD
                      </div>
                    )}
                  </div>
                );
              })}
              <label
                className={`aspect-square flex flex-col items-center justify-center border-2 border-dashed rounded-lg cursor-pointer transition-colors ${isDark ? "border-white/[0.08] hover:border-white/20 hover:bg-white/[0.02]" : "border-gray-200 hover:border-gray-400 hover:bg-gray-50"}`}
              >
                <Upload
                  className={`w-5 h-5 mb-1 ${isDark ? "text-gray-600" : "text-gray-400"}`}
                />
                <span
                  className={`text-[10px] font-medium ${isDark ? "text-gray-500" : "text-gray-500"}`}
                >
                  Upload
                </span>
                <input
                  type="file"
                  multiple
                  accept="image/*,.heic,.heif,video/mp4,video/webm,video/ogg,video/quicktime"
                  className="hidden"
                  onChange={(e) => {
                    if (e.target.files?.length)
                      onEditField(field, Array.from(e.target.files));
                  }}
                />
              </label>
            </div>
          </div>
        );
      case FIELD_TYPES.VIDEO:
        const videoIds = Array.isArray(value) ? value : value ? [value] : [];
        return (
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              {videoIds.map((val, idx) => {
                let src = null;
                let isUploading = false;

                if (val instanceof File) {
                  src = URL.createObjectURL(val);
                  isUploading = true;
                } else if (typeof val === "string") {
                  if (val.startsWith("videos/") || val.startsWith("images/"))
                    src = `${S3_BASE_URL}/${val}`;
                  else if (val.startsWith("http")) src = val;
                }

                return (
                  <div
                    key={idx}
                    className="aspect-video relative group bg-gray-900 rounded-lg overflow-hidden border border-gray-100"
                  >
                    {src ? (
                      <>
                        <video
                          src={src}
                          className={`w-full h-full object-contain ${isUploading ? "opacity-50" : ""}`}
                          controls
                          muted
                          playsInline
                          preload="metadata"
                        />
                        {isUploading && (
                          <div className="absolute inset-0 flex items-center justify-center bg-black/30">
                            <Loader2 className="w-6 h-6 text-white animate-spin" />
                          </div>
                        )}
                      </>
                    ) : (
                      <div className="flex items-center justify-center w-full h-full bg-gray-50">
                        <Video className="text-gray-300 w-6 h-6" />
                      </div>
                    )}
                    <button
                      onClick={() => onDeleteImage(field, val)}
                      className="absolute top-1 right-1 bg-white/90 p-1 rounded-full text-red-500 opacity-0 group-hover:opacity-100 transition-opacity z-10"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </div>
                );
              })}
              <label
                className={`aspect-video flex flex-col items-center justify-center border-2 border-dashed rounded-lg cursor-pointer transition-colors ${isDark ? "border-white/[0.08] hover:border-white/20 hover:bg-white/[0.02]" : "border-gray-200 hover:border-gray-400 hover:bg-gray-50"}`}
              >
                <Upload
                  className={`w-5 h-5 mb-1 ${isDark ? "text-gray-600" : "text-gray-400"}`}
                />
                <span
                  className={`text-[10px] font-medium ${isDark ? "text-gray-500" : "text-gray-500"}`}
                >
                  Upload Video
                </span>
                <span
                  className={`text-[9px] mt-0.5 ${isDark ? "text-gray-600" : "text-gray-400"}`}
                >
                  mp4, webm, ogg, mov
                </span>
                <input
                  type="file"
                  multiple
                  accept="video/mp4,video/webm,video/ogg,video/quicktime,.mp4,.webm,.ogg,.mov,.avi,.mkv"
                  className="hidden"
                  onChange={(e) => {
                    if (e.target.files?.length)
                      onEditField(field, Array.from(e.target.files));
                  }}
                />
              </label>
            </div>
          </div>
        );
      case FIELD_TYPES.DOCUMENT:
        const docIds = Array.isArray(value) ? value : value ? [value] : [];
        return (
          <div className="space-y-2">
            {docIds.map((val, idx) => {
              let href = null;
              let label = `Document ${idx + 1}`;
              let isUploading = false;

              if (val instanceof File) {
                href = URL.createObjectURL(val);
                label = val.name;
                isUploading = true;
              } else if (typeof val === "string") {
                if (val.startsWith("documents/"))
                  href = `${S3_BASE_URL}/${val}`;
                else if (val.startsWith("http")) href = val;
                label = prettyDocumentName(val);
              }

              return (
                <div
                  key={idx}
                  className={`flex items-center gap-3 p-2.5 rounded-lg border ${isDark ? "bg-white/[0.02] border-white/[0.06]" : "bg-gray-50 border-gray-100"}`}
                >
                  <div
                    className={`flex-shrink-0 w-8 h-8 rounded flex items-center justify-center ${isDark ? "bg-white/[0.06] text-gray-300" : "bg-white text-red-500 border border-gray-200"}`}
                  >
                    {isUploading ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <FileText className="w-4 h-4" />
                    )}
                  </div>
                  {href && !isUploading ? (
                    <a
                      href={href}
                      target="_blank"
                      rel="noopener noreferrer"
                      className={`flex-1 truncate text-[13px] hover:underline ${isDark ? "text-gray-200" : "text-gray-700"}`}
                      title={label}
                    >
                      {label}
                    </a>
                  ) : (
                    <span
                      className={`flex-1 truncate text-[13px] ${isDark ? "text-gray-400" : "text-gray-500"}`}
                      title={label}
                    >
                      {label}
                      {isUploading && " (uploaden…)"}
                    </span>
                  )}
                  <button
                    onClick={() => onDeleteImage(field, val)}
                    className={`flex-shrink-0 p-1 rounded-full transition-colors ${isDark ? "text-gray-600 hover:text-red-400" : "text-gray-300 hover:text-red-500"}`}
                  >
                    <X className="w-3.5 h-3.5" />
                  </button>
                </div>
              );
            })}
            <label
              className={`flex items-center justify-center gap-2 h-9 border-2 border-dashed rounded-lg cursor-pointer transition-colors ${isDark ? "border-white/[0.08] hover:border-white/20 hover:bg-white/[0.02]" : "border-gray-200 hover:border-gray-400 hover:bg-gray-50"}`}
            >
              <Upload
                className={`w-4 h-4 ${isDark ? "text-gray-600" : "text-gray-400"}`}
              />
              <span
                className={`text-[12px] font-medium ${isDark ? "text-gray-500" : "text-gray-500"}`}
              >
                PDF uploaden
              </span>
              <input
                type="file"
                multiple
                accept="application/pdf,.pdf"
                className="hidden"
                onChange={(e) => {
                  if (e.target.files?.length)
                    onEditField(field, Array.from(e.target.files));
                }}
              />
            </label>
          </div>
        );
      case FIELD_TYPES.DATE:
        // Ensure value is YYYY-MM-DD for the input to work correctly with native picker
        let dateValue = value || "";
        // If data was stored as dd/mm/yyyy, convert to YYYY-MM-DD for the input
        if (dateValue && /^\d{2}\/\d{2}\/\d{4}$/.test(dateValue)) {
          const [d, m, y] = dateValue.split("/");
          dateValue = `${y}-${m}-${d}`;
        }

        return (
          <div className="relative">
            <Input
              type="date"
              value={dateValue}
              onChange={(e) => onEditField(field, e.target.value)}
              className={`${inputClass} pl-10 cursor-pointer`}
            />
            <Calendar
              className={`absolute left-3 top-2.5 w-4 h-4 pointer-events-none ${isDark ? "text-gray-600" : "text-gray-400"}`}
            />
          </div>
        );
      case FIELD_TYPES.ARRAY:
        const listItems = Array.isArray(value) ? value : [];
        return (
          <div className="space-y-2">
            {listItems.map((item, idx) => {
              const displayVal =
                useTranslations && typeof item === "object" && item !== null
                  ? item[currentLanguage] || ""
                  : item;
              return (
                <div key={idx} className="flex gap-2">
                  <Input
                    value={displayVal}
                    onChange={(e) => {
                      const newList = [...listItems];
                      if (useTranslations) {
                        const oldItem =
                          typeof newList[idx] === "object" &&
                          newList[idx] !== null
                            ? newList[idx]
                            : { nl: "", fr: "", en: "" };
                        newList[idx] = {
                          ...oldItem,
                          [currentLanguage]: e.target.value,
                        };
                      } else {
                        newList[idx] = e.target.value;
                      }
                      onEditField(field, newList);
                    }}
                    className={inputClass}
                  />
                  <Button
                    variant="ghost"
                    size="icon"
                    className={`h-9 w-9 ${isDark ? "text-red-400 hover:text-red-300 hover:bg-red-500/10" : "text-red-400 hover:text-red-500 hover:bg-red-50"}`}
                    onClick={() => {
                      const newList = listItems.filter((_, i) => i !== idx);
                      onEditField(field, newList);
                    }}
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              );
            })}
            <Button
              variant="outline"
              size="sm"
              onClick={() =>
                onEditField(field, [
                  ...listItems,
                  useTranslations ? { nl: "", fr: "", en: "" } : "",
                ])
              }
              className={`text-[12px] w-full border-dashed ${isDark ? "border-white/[0.08] text-gray-500 hover:bg-white/[0.04]" : "border-gray-200 text-gray-500 hover:bg-gray-50"}`}
            >
              + Item toevoegen
            </Button>
          </div>
        );
      default:
        return (
          <Input
            value={displayValue || ""}
            onChange={(e) => handleChange(e.target.value)}
            className={inputClass}
          />
        );
    }
  };

  if (!editedItems) return null;
  const grouped = getGroupedFields();

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className={`sm:max-w-6xl w-full h-full sm:w-[95vw] sm:h-[90vh] flex flex-col p-0 gap-0 overflow-hidden rounded-none sm:rounded-xl border-0 ${isDark ? "bg-[#0a0a0a]" : "bg-[#f8f8fa]"}`}
      >
        {/* Header */}
        <div
          className={`h-12 sm:h-14 flex items-center justify-between px-3 sm:px-6 flex-shrink-0 z-10 border-b ${isDark ? "bg-[#141414] border-white/[0.06]" : "bg-white border-gray-100"}`}
        >
          <div className="flex items-center gap-2 sm:gap-3 min-w-0">
            <Button
              variant="ghost"
              size="icon"
              onClick={onCancel}
              className={`h-8 w-8 rounded-lg shrink-0 ${isDark ? "text-gray-500 hover:text-white hover:bg-white/[0.06]" : "text-gray-400 hover:text-gray-900 hover:bg-gray-100"}`}
            >
              <ChevronLeft className="w-4 h-4" />
            </Button>
            <div className="min-w-0">
              <h2
                className={`text-[13px] sm:text-[14px] font-semibold leading-none truncate ${isDark ? "text-white" : "text-gray-900"}`}
              >
                {isAddingNewItem(editedItems.category)
                  ? "Nieuw Item"
                  : "Item Bewerken"}
              </h2>
              <div className="flex items-center gap-2 mt-1">
                <span
                  className={`px-1.5 py-0.5 rounded text-[10px] font-medium uppercase tracking-wide ${isDark ? "bg-white/[0.06] text-gray-500" : "bg-gray-100 text-gray-500"}`}
                >
                  {editedItems.category}
                </span>
                {useTranslations && (
                  <span
                    className={`px-1.5 py-0.5 rounded text-[10px] font-medium uppercase ${isDark ? "bg-white/[0.06] text-gray-400" : "bg-gray-100 text-gray-500"}`}
                  >
                    {currentLanguage}
                  </span>
                )}
              </div>
            </div>
          </div>

          <div className="flex items-center gap-2 shrink-0">
            {useTranslations && (
              <div
                className={`hidden sm:flex p-0.5 rounded-lg mr-2 ${isDark ? "bg-white/[0.04]" : "bg-gray-100"}`}
              >
                {availableLanguages.map((lang) => (
                  <button
                    key={lang}
                    onClick={() => setCurrentLanguage(lang)}
                    className={`px-2.5 py-1 text-[11px] font-semibold uppercase rounded-md transition-all ${
                      currentLanguage === lang
                        ? isDark
                          ? "bg-white/[0.08] text-white"
                          : "bg-white text-gray-900 shadow-sm"
                        : isDark
                          ? "text-gray-500 hover:text-gray-300"
                          : "text-gray-400 hover:text-gray-600"
                    }`}
                  >
                    {lang}
                  </button>
                ))}
                <button
                  onClick={() => setAddLanguageDialogOpen(true)}
                  className={`px-2 ${isDark ? "text-gray-600 hover:text-gray-400" : "text-gray-400 hover:text-gray-600"}`}
                >
                  <PlusCircle className="w-3.5 h-3.5" />
                </button>
              </div>
            )}
            <Button
              variant="outline"
              onClick={onCancel}
              className={`h-8 rounded-lg text-[12px] hidden sm:inline-flex ${isDark ? "border-white/[0.08] text-gray-400 hover:bg-white/[0.04]" : "border-gray-200 text-gray-600 hover:bg-gray-50"}`}
            >
              Annuleren
            </Button>
            <Button
              onClick={onSave}
              className={`h-8 rounded-lg text-[12px] font-medium gap-2 hidden sm:inline-flex min-w-[100px] ${isDark ? "bg-white text-black hover:bg-gray-200" : "bg-gray-900 text-white hover:bg-gray-800"}`}
            >
              <Save className="w-3.5 h-3.5" /> Opslaan
            </Button>
          </div>
        </div>

        {/* Mobile language switcher bar */}
        {useTranslations && (
          <div
            className={`sm:hidden flex items-center gap-2 px-3 py-2 border-b overflow-x-auto ${isDark ? "bg-[#111] border-white/[0.06]" : "bg-gray-50 border-gray-100"}`}
          >
            <span
              className={`text-[10px] uppercase tracking-wider font-semibold shrink-0 ${isDark ? "text-gray-600" : "text-gray-400"}`}
            >
              Taal:
            </span>
            <div
              className={`flex p-0.5 rounded-lg ${isDark ? "bg-white/[0.04]" : "bg-white"}`}
            >
              {availableLanguages.map((lang) => (
                <button
                  key={lang}
                  onClick={() => setCurrentLanguage(lang)}
                  className={`px-2.5 py-1 text-[11px] font-semibold uppercase rounded-md transition-all ${
                    currentLanguage === lang
                      ? isDark
                        ? "bg-white/[0.08] text-white"
                        : "bg-gray-100 text-gray-900"
                      : isDark
                        ? "text-gray-500"
                        : "text-gray-400"
                  }`}
                >
                  {lang}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Start Content - scrollable */}

        {/* Upload Progress Overlay */}
        {uploadProgress && (
          <div className="absolute inset-0 z-50 bg-black/40 backdrop-blur-sm flex items-center justify-center">
            <div
              className={`rounded-xl shadow-2xl p-8 max-w-sm w-full mx-4 space-y-4 ${isDark ? "bg-[#141414]" : "bg-white"}`}
            >
              <div className="flex items-center gap-3">
                <Loader2
                  className={`w-6 h-6 animate-spin ${isDark ? "text-white" : "text-gray-900"}`}
                />
                <div>
                  <h3
                    className={`text-[13px] font-semibold ${isDark ? "text-white" : "text-gray-900"}`}
                  >
                    Bestand uploaden...
                  </h3>
                  <p
                    className={`text-[11px] mt-0.5 truncate max-w-[200px] ${isDark ? "text-gray-500" : "text-gray-500"}`}
                  >
                    {uploadProgress.fileName}
                    {uploadProgress.total > 1 && (
                      <span
                        className={isDark ? "text-gray-600" : "text-gray-400"}
                      >
                        {" "}
                        ({uploadProgress.current}/{uploadProgress.total})
                      </span>
                    )}
                  </p>
                </div>
              </div>
              <div className="space-y-2">
                <div
                  className={`w-full rounded-full h-2 overflow-hidden ${isDark ? "bg-white/[0.06]" : "bg-gray-100"}`}
                >
                  <div
                    className="h-full rounded-full transition-all duration-300 ease-out"
                    style={{
                      width: `${uploadProgress.percent}%`,
                      background: isDark
                        ? uploadProgress.percent < 100
                          ? "#fff"
                          : "#4ade80"
                        : uploadProgress.percent < 100
                          ? "#111"
                          : "#22C55E",
                    }}
                  />
                </div>
                <div className="flex justify-between items-center">
                  <span
                    className={`text-[11px] ${isDark ? "text-gray-600" : "text-gray-400"}`}
                  >
                    {uploadProgress.percent < 100
                      ? "Uploaden..."
                      : "Verwerken..."}
                  </span>
                  <span
                    className={`text-[12px] font-semibold ${isDark ? "text-white" : "text-gray-900"}`}
                  >
                    {uploadProgress.percent}%
                  </span>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Upload Error Overlay */}
        {uploadError && !uploadProgress && (
          <div className="absolute inset-0 z-50 bg-black/40 backdrop-blur-sm flex items-center justify-center">
            <div
              className={`rounded-xl shadow-2xl p-6 max-w-sm w-full mx-4 space-y-4 ${isDark ? "bg-[#141414]" : "bg-white"}`}
            >
              <div className="flex items-start gap-3">
                <AlertCircle className="w-6 h-6 shrink-0 text-red-500" />
                <div>
                  <h3
                    className={`text-[13px] font-semibold ${isDark ? "text-white" : "text-gray-900"}`}
                  >
                    Uploaden mislukt
                  </h3>
                  <p
                    className={`text-[12px] mt-1 ${isDark ? "text-gray-400" : "text-gray-600"}`}
                  >
                    {uploadError}
                  </p>
                </div>
              </div>
              <div className="flex justify-end">
                <button
                  type="button"
                  onClick={onDismissUploadError}
                  className={`text-[12px] font-medium px-3 py-1.5 rounded-lg ${isDark ? "bg-white/10 hover:bg-white/20 text-white" : "bg-gray-100 hover:bg-gray-200 text-gray-900"}`}
                >
                  Sluiten
                </button>
              </div>
            </div>
          </div>
        )}
        <div className="flex-1 overflow-y-auto p-4 sm:p-8 pb-20 sm:pb-8">
          <div className="max-w-7xl mx-auto grid grid-cols-1 md:grid-cols-12 gap-5 sm:gap-8">
            {/* LEFT COLUMN: Main Editing (8/12) */}
            <div className="md:col-span-8 flex flex-col gap-5 sm:gap-8">
              {/* Block 1: Basics */}
              <section
                className={`rounded-xl border overflow-hidden ${isDark ? "bg-white/[0.03] border-white/[0.06]" : "bg-white border-gray-200/80"}`}
              >
                <div
                  className={`px-4 sm:px-6 py-3 sm:py-4 border-b flex justify-between items-center ${isDark ? "border-white/[0.06] bg-white/[0.02]" : "border-gray-100 bg-gray-50/30"}`}
                >
                  <h3
                    className={`text-[11px] font-semibold uppercase tracking-wider ${isDark ? "text-gray-500" : "text-gray-400"}`}
                  >
                    Basis Informatie
                  </h3>
                </div>
                <div className="p-4 sm:p-6 space-y-4 sm:space-y-6">
                  <div>
                    <label
                      className={`block text-[12px] font-medium mb-1.5 ${isDark ? "text-gray-400" : "text-gray-600"}`}
                    >
                      Naam
                    </label>
                    <Input
                      value={
                        useTranslations
                          ? getTranslatedValue("name", editedItems.item.name)
                          : editedItems.item.name || ""
                      }
                      onChange={(e) => {
                        if (useTranslations)
                          setTranslatedValue("name", e.target.value);
                        else onEditField("name", e.target.value);
                      }}
                      className={`h-9 text-[13px] rounded-lg ${isDark ? "bg-white/[0.04] border-white/[0.06] text-white placeholder-gray-600" : "bg-white border-gray-200 text-gray-900 placeholder-gray-400"}`}
                      placeholder="Productnaam"
                    />
                  </div>

                  {/* Textareas */}
                  {grouped.largeText.map((field) => (
                    <div key={field} className="group">
                      <div className="flex justify-between items-center mb-1.5">
                        <label
                          className={`text-[12px] font-medium ${isDark ? "text-gray-400" : "text-gray-600"}`}
                        >
                          {getFieldDisplayName(field)}
                        </label>
                        <button
                          onClick={() => {
                            setFieldToDelete(field);
                            setDeleteFieldDialogOpen(true);
                          }}
                          className={`text-[11px] sm:opacity-0 sm:group-hover:opacity-100 transition-opacity ${isDark ? "text-red-400 hover:text-red-300" : "text-red-500 hover:text-red-700"}`}
                        >
                          Verwijder
                        </button>
                      </div>
                      {renderFieldInput(field, editedItems.item[field])}
                    </div>
                  ))}
                </div>
              </section>

              {/* Block 2: Media */}
              <section
                className={`rounded-xl border overflow-hidden ${isDark ? "bg-white/[0.03] border-white/[0.06]" : "bg-white border-gray-200/80"}`}
              >
                <div
                  className={`px-4 sm:px-6 py-3 sm:py-4 border-b flex justify-between items-center ${isDark ? "border-white/[0.06] bg-white/[0.02]" : "border-gray-100 bg-gray-50/30"}`}
                >
                  <h3
                    className={`text-[11px] font-semibold uppercase tracking-wider ${isDark ? "text-gray-500" : "text-gray-400"}`}
                  >
                    Media & Bestanden
                  </h3>
                </div>
                <div className="p-4 sm:p-6">
                  {grouped.images.length === 0 ? (
                    <div
                      className={`text-center py-6 border-2 border-dashed rounded-lg ${isDark ? "border-white/[0.06]" : "border-gray-200"}`}
                    >
                      <p
                        className={`text-[13px] mb-3 ${isDark ? "text-gray-600" : "text-gray-400"}`}
                      >
                        Nog geen media velden aangemaakt.
                      </p>
                      <div className="flex gap-2 justify-center">
                        <button
                          onClick={() => {
                            setNewFieldType(FIELD_TYPES.IMAGE);
                            setAddFieldDialogOpen(true);
                          }}
                          className={`h-8 px-3 rounded-lg text-[12px] font-medium border transition-colors ${isDark ? "border-white/[0.08] text-gray-400 hover:bg-white/[0.04]" : "border-gray-200 text-gray-600 hover:bg-gray-50"}`}
                        >
                          + Afbeeldingenveld
                        </button>
                        <button
                          onClick={() => {
                            setNewFieldType(FIELD_TYPES.VIDEO);
                            setAddFieldDialogOpen(true);
                          }}
                          className={`h-8 px-3 rounded-lg text-[12px] font-medium border transition-colors ${isDark ? "border-white/[0.08] text-gray-400 hover:bg-white/[0.04]" : "border-gray-200 text-gray-600 hover:bg-gray-50"}`}
                        >
                          + Videoveld
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div className="space-y-6">
                      {grouped.images.map((field) => (
                        <div key={field} className="group">
                          <div className="flex justify-between mb-3 items-center">
                            <span
                              className={`text-[13px] font-medium ${isDark ? "text-white" : "text-gray-900"}`}
                            >
                              {getFieldDisplayName(field)}
                            </span>
                            <button
                              onClick={() => {
                                setFieldToDelete(field);
                                setDeleteFieldDialogOpen(true);
                              }}
                              className={`text-[11px] sm:opacity-0 sm:group-hover:opacity-100 transition-opacity ${isDark ? "text-red-400 hover:text-red-300" : "text-red-400 hover:text-red-500"}`}
                            >
                              Verwijder
                            </button>
                          </div>
                          {renderFieldInput(field, editedItems.item[field])}
                        </div>
                      ))}
                      <div className="pt-2 flex gap-4">
                        <button
                          onClick={() => {
                            setNewFieldType(FIELD_TYPES.IMAGE);
                            setAddFieldDialogOpen(true);
                          }}
                          className={`text-[12px] font-medium hover:underline ${isDark ? "text-gray-400" : "text-gray-500"}`}
                        >
                          + Extra afbeeldingsgroep
                        </button>
                        <button
                          onClick={() => {
                            setNewFieldType(FIELD_TYPES.VIDEO);
                            setAddFieldDialogOpen(true);
                          }}
                          className={`text-[12px] font-medium hover:underline ${isDark ? "text-gray-400" : "text-gray-500"}`}
                        >
                          + Videogroep toevoegen
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </section>
            </div>

            {/* RIGHT COLUMN: Sidebar (4/12) */}
            <div className="md:col-span-4 space-y-5 sm:space-y-8">
              {/* Sidebar 1: Status */}
              <div
                className={`rounded-xl border overflow-hidden ${isDark ? "bg-white/[0.03] border-white/[0.06]" : "bg-white border-gray-200/80"}`}
              >
                <div className="p-4 sm:p-5">
                  <div className="flex items-center justify-between mb-4">
                    <h3
                      className={`text-[11px] font-semibold uppercase tracking-wider ${isDark ? "text-gray-500" : "text-gray-400"}`}
                    >
                      Publicatie
                    </h3>
                    <div
                      className={`h-2 w-2 rounded-full ${editedItems.item.isVisible !== false ? "bg-green-500" : isDark ? "bg-gray-600" : "bg-gray-300"}`}
                    ></div>
                  </div>

                  <div className="flex items-center justify-between py-2">
                    <span
                      className={`text-[13px] font-medium ${isDark ? "text-gray-300" : "text-gray-700"}`}
                    >
                      Zichtbaarheid
                    </span>
                    <div
                      className={`flex items-center rounded-lg p-0.5 ${isDark ? "bg-white/[0.06]" : "bg-gray-100"}`}
                    >
                      <button
                        onClick={() => onEditField("isVisible", true)}
                        className={`px-3 py-1 text-[12px] font-medium rounded-md transition-all ${
                          editedItems.item.isVisible !== false
                            ? isDark
                              ? "bg-white/[0.1] text-white"
                              : "bg-white shadow-sm text-gray-900"
                            : isDark
                              ? "text-gray-500 hover:text-gray-300"
                              : "text-gray-500 hover:text-gray-700"
                        }`}
                      >
                        Aan
                      </button>
                      <button
                        onClick={() => onEditField("isVisible", false)}
                        className={`px-3 py-1 text-[12px] font-medium rounded-md transition-all ${
                          editedItems.item.isVisible === false
                            ? isDark
                              ? "bg-white/[0.1] text-white"
                              : "bg-white shadow-sm text-gray-900"
                            : isDark
                              ? "text-gray-500 hover:text-gray-300"
                              : "text-gray-500 hover:text-gray-700"
                        }`}
                      >
                        Uit
                      </button>
                    </div>
                  </div>
                </div>
              </div>

              {/* Sidebar 2: Properties */}
              <div
                className={`rounded-xl border overflow-hidden flex flex-col ${isDark ? "bg-white/[0.03] border-white/[0.06]" : "bg-white border-gray-200/80"}`}
              >
                <div
                  className={`px-4 sm:px-5 py-3 border-b flex justify-between items-center ${isDark ? "border-white/[0.06] bg-white/[0.02]" : "border-gray-100 bg-gray-50/30"}`}
                >
                  <h3
                    className={`text-[11px] font-semibold uppercase tracking-wider ${isDark ? "text-gray-500" : "text-gray-400"}`}
                  >
                    Eigenschappen
                  </h3>
                </div>
                <div className="p-4 sm:p-5 space-y-4 sm:space-y-5 flex-1">
                  {grouped.extra.map((field, idx) => {
                    const metadata = getFieldMetadata(field);
                    return (
                      <div
                        key={field}
                        className="group"
                        onDragOver={(e) => e.preventDefault()}
                        onDrop={(e) => {
                          e.preventDefault();
                          const source = e.dataTransfer.getData("text/plain");
                        }}
                      >
                        <div className="flex items-center justify-between mb-1.5">
                          <div className="flex items-center gap-2 overflow-hidden">
                            <GripVertical
                              className={`w-3 h-3 cursor-grab active:cursor-grabbing flex-shrink-0 ${isDark ? "text-gray-600" : "text-gray-300"}`}
                              draggable
                              onDragStart={(e) =>
                                e.dataTransfer.setData("text/plain", field)
                              }
                            />
                            <label
                              className={`text-[12px] font-medium truncate ${isDark ? "text-gray-300" : "text-gray-700"}`}
                              title={getFieldDisplayName(field)}
                            >
                              {getFieldDisplayName(field)}
                            </label>
                          </div>
                          <div className="flex items-center gap-1.5">
                            {isAdmin &&
                              metadata.type === FIELD_TYPES.SELECT &&
                              field.includes("[") &&
                              onConvertSelectToCategory && (
                                <button
                                  onClick={() => {
                                    setLinkSelectField(field);
                                    setLinkSelectCategory("");
                                    setLinkSelectDialogOpen(true);
                                  }}
                                  title="Koppel deze keuzelijst aan een categorie"
                                  className={`sm:opacity-0 sm:group-hover:opacity-100 transition-opacity ${isDark ? "text-gray-600 hover:text-blue-400" : "text-gray-300 hover:text-blue-500"}`}
                                >
                                  <Link2 className="w-3 h-3" />
                                </button>
                              )}
                            <button
                              onClick={() => {
                                setFieldToDelete(field);
                                setDeleteFieldDialogOpen(true);
                              }}
                              className={`sm:opacity-0 sm:group-hover:opacity-100 transition-opacity ${isDark ? "text-gray-600 hover:text-red-400" : "text-gray-300 hover:text-red-500"}`}
                            >
                              <Trash2 className="w-3 h-3" />
                            </button>
                          </div>
                        </div>
                        {renderFieldInput(field, editedItems.item[field])}
                      </div>
                    );
                  })}

                  <button
                    onClick={() => setAddFieldDialogOpen(true)}
                    className={`w-full text-[12px] h-9 rounded-lg border border-dashed font-medium transition-colors ${isDark ? "border-white/[0.08] text-gray-500 hover:text-gray-300 hover:bg-white/[0.02]" : "border-gray-300 text-gray-500 hover:text-gray-900 hover:bg-gray-50"}`}
                  >
                    + Veld toevoegen
                  </button>
                </div>
              </div>

              {/* Sidebar 3: Settings */}
              <div
                className={`rounded-xl border overflow-hidden ${isDark ? "bg-white/[0.03] border-white/[0.06]" : "bg-white border-gray-200/80"}`}
              >
                <div
                  className={`px-4 sm:px-5 py-3 border-b ${isDark ? "border-white/[0.06] bg-white/[0.02]" : "border-gray-100 bg-gray-50/30"}`}
                >
                  <h3
                    className={`text-[11px] font-semibold uppercase tracking-wider ${isDark ? "text-gray-500" : "text-gray-400"}`}
                  >
                    Instellingen
                  </h3>
                </div>
                <div className="p-4 sm:p-5">
                  <div className="flex items-center justify-between py-1">
                    <span
                      className={`text-[13px] font-medium ${isDark ? "text-gray-300" : "text-gray-700"}`}
                    >
                      Meertaligheid
                    </span>
                    <button
                      onClick={() => handleToggleTranslations(!useTranslations)}
                      className={`w-9 h-5 rounded-full p-1 transition-colors ${useTranslations ? (isDark ? "bg-white" : "bg-gray-900") : isDark ? "bg-white/[0.1]" : "bg-gray-200"}`}
                    >
                      <div
                        className={`w-3 h-3 rounded-full shadow-sm transition-transform ${useTranslations ? "translate-x-4" : ""} ${useTranslations ? (isDark ? "bg-black" : "bg-white") : "bg-white"}`}
                      ></div>
                    </button>
                  </div>
                  {useTranslations && (
                    <div
                      className={`mt-4 pt-4 border-t ${isDark ? "border-white/[0.06]" : "border-gray-100"}`}
                    >
                      <p
                        className={`text-[12px] mb-2 ${isDark ? "text-gray-500" : "text-gray-400"}`}
                      >
                        Talen beheer
                      </p>
                      <div className="flex flex-wrap gap-2 mb-3">
                        {availableLanguages.map((l) => (
                          <span
                            key={l}
                            className={`px-2 py-1 rounded text-[11px] uppercase font-medium border ${isDark ? "bg-white/[0.04] border-white/[0.06] text-gray-400" : "bg-gray-50 border-gray-200 text-gray-600"}`}
                          >
                            {l}
                          </span>
                        ))}
                        <button
                          onClick={() => setAddLanguageDialogOpen(true)}
                          className={`px-2 py-1 border border-dashed rounded text-[11px] transition-colors ${isDark ? "border-white/[0.1] text-gray-600 hover:text-gray-400" : "border-gray-300 text-gray-400 hover:text-gray-600"}`}
                        >
                          +
                        </button>
                      </div>
                      <button
                        onClick={openAutoTranslateDialog}
                        className={`w-full h-8 rounded-lg text-[12px] font-medium flex items-center justify-center gap-2 border transition-colors ${isDark ? "border-white/[0.08] text-gray-400 hover:bg-white/[0.04]" : "border-gray-200 text-gray-600 hover:bg-gray-50"}`}
                      >
                        <Sparkles className="w-3.5 h-3.5" /> Auto-Translate
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Dialogs */}
        <NestedDialog
          open={addFieldDialogOpen}
          onOpenChange={setAddFieldDialogOpen}
        >
          <NestedDialogContent
            className={`sm:max-w-md max-h-[90vh] flex flex-col p-0 gap-0 overflow-hidden rounded-xl border font-sans ${isDark ? "bg-[#141414] border-white/[0.06]" : "bg-white border-gray-200/80"}`}
          >
            <div
              className={`px-4 sm:px-6 py-3 sm:py-4 border-b shrink-0 ${isDark ? "border-white/[0.06]" : "border-gray-100"}`}
            >
              <h3
                className={`text-[14px] font-semibold ${isDark ? "text-white" : "text-gray-900"}`}
              >
                Nieuw veld toevoegen
              </h3>
            </div>

            <div className="p-4 sm:p-6 space-y-5 overflow-y-auto flex-1">
              <div>
                <label
                  className={`text-[11px] font-semibold uppercase tracking-wider block mb-2 ${isDark ? "text-gray-500" : "text-gray-400"}`}
                >
                  Naam
                </label>
                <Input
                  value={newFieldName}
                  onChange={(e) => setNewFieldName(e.target.value)}
                  placeholder="Bijv. Gewicht, Kleur..."
                  className={`h-9 text-[13px] rounded-lg ${isDark ? "bg-white/[0.04] border-white/[0.06] text-white placeholder-gray-600" : "bg-white border-gray-200 text-gray-900 placeholder-gray-400"}`}
                  autoFocus
                />
              </div>

              <div>
                <label
                  className={`text-[11px] font-semibold uppercase tracking-wider block mb-2 ${isDark ? "text-gray-500" : "text-gray-400"}`}
                >
                  Type
                </label>
                <div className="grid grid-cols-2 gap-2">
                  {[
                    {
                      type: FIELD_TYPES.TEXT,
                      label: "Korte tekst",
                      icon: FileText,
                    },
                    {
                      type: FIELD_TYPES.TEXTAREA,
                      label: "Lange tekst",
                      icon: LayoutGrid,
                    },
                    { type: FIELD_TYPES.NUMBER, label: "Getal", icon: Tag },
                    {
                      type: FIELD_TYPES.IMAGE,
                      label: "Afbeelding",
                      icon: ImageIcon,
                    },
                    {
                      type: FIELD_TYPES.SELECT,
                      label: "Selectielijst",
                      icon: List,
                    },
                    {
                      name: "Schakelaar",
                      type: FIELD_TYPES.CHECKBOX,
                      icon: CheckSquare,
                    },
                    { name: "Datum", type: FIELD_TYPES.DATE, icon: Calendar },
                    {
                      type: FIELD_TYPES.VIDEO,
                      label: "Video",
                      icon: Video,
                    },
                    {
                      type: FIELD_TYPES.DOCUMENT,
                      label: "Document (PDF)",
                      icon: FileText,
                    },
                    { name: "Eenheid", type: FIELD_TYPES.UNIT, icon: Scale },
                    { name: "Lijst", type: FIELD_TYPES.ARRAY, icon: ListPlus },
                  ].map((opt) => (
                    <button
                      key={opt.type}
                      onClick={() => setNewFieldType(opt.type)}
                      className={`flex items-center gap-3 p-3 rounded-lg border text-[13px] text-left transition-all ${
                        newFieldType === opt.type
                          ? isDark
                            ? "border-white/[0.15] bg-white/[0.06] text-white font-medium"
                            : "border-gray-900 bg-gray-50 text-gray-900 font-medium"
                          : isDark
                            ? "border-white/[0.06] hover:border-white/[0.1] hover:bg-white/[0.02] text-gray-400"
                            : "border-gray-200 hover:border-gray-300 hover:bg-gray-50 text-gray-600"
                      }`}
                    >
                      <opt.icon
                        className={`w-4 h-4 ${newFieldType === opt.type ? (isDark ? "text-white" : "text-gray-900") : isDark ? "text-gray-600" : "text-gray-400"}`}
                      />{" "}
                      {opt.label || opt.name}
                    </button>
                  ))}
                </div>
              </div>

              {newFieldType === FIELD_TYPES.UNIT && (
                <Input
                  value={newFieldUnit}
                  onChange={(e) => setNewFieldUnit(e.target.value)}
                  placeholder="Eenheid (bv. kg)"
                  className={`mt-2 h-9 text-[13px] rounded-lg ${isDark ? "bg-white/[0.04] border-white/[0.06] text-white placeholder-gray-600" : "bg-white border-gray-200 text-gray-900 placeholder-gray-400"}`}
                />
              )}

              {/* Simplified Select Options Logic */}
              {newFieldType === FIELD_TYPES.SELECT && (
                <div
                  className={`space-y-3 p-3 rounded-lg border ${isDark ? "bg-white/[0.02] border-white/[0.06]" : "bg-gray-50 border-gray-100"}`}
                >
                  <div
                    className={`flex gap-2 p-1 rounded-md border ${isDark ? "bg-white/[0.04] border-white/[0.06]" : "bg-white border-gray-200"}`}
                  >
                    <button
                      onClick={() => setSelectSource("manual")}
                      className={`flex-1 text-[12px] py-1.5 rounded font-medium ${selectSource === "manual" ? (isDark ? "bg-white/[0.08] text-white" : "bg-gray-100 text-gray-900") : isDark ? "text-gray-500 hover:text-gray-300" : "text-gray-500 hover:text-gray-900"}`}
                    >
                      Handmatige opties
                    </button>
                    <button
                      onClick={() => setSelectSource("category")}
                      className={`flex-1 text-[12px] py-1.5 rounded font-medium ${selectSource === "category" ? (isDark ? "bg-white/[0.08] text-white" : "bg-gray-100 text-gray-900") : isDark ? "text-gray-500 hover:text-gray-300" : "text-gray-500 hover:text-gray-900"}`}
                    >
                      Uit Categorie
                    </button>
                  </div>

                  {selectSource === "manual" ? (
                    <>
                      <p
                        className={`text-[11px] font-semibold uppercase ${isDark ? "text-gray-500" : "text-gray-400"}`}
                      >
                        Opties definieren
                      </p>
                      {selectOptions.map((opt, i) => (
                        <div key={i} className="flex gap-2">
                          <Input
                            value={opt}
                            onChange={(e) => {
                              const newOpts = [...selectOptions];
                              newOpts[i] = e.target.value;
                              setSelectOptions(newOpts);
                            }}
                            placeholder={`Optie ${i + 1}`}
                            className={`h-8 text-[13px] rounded-lg ${isDark ? "bg-white/[0.04] border-white/[0.06] text-white placeholder-gray-600" : "bg-white border-gray-200 text-gray-900 placeholder-gray-400"}`}
                          />
                        </div>
                      ))}
                      <button
                        onClick={() => setSelectOptions([...selectOptions, ""])}
                        className={`w-full h-7 text-[12px] font-medium rounded-lg transition-colors ${isDark ? "text-gray-400 hover:bg-white/[0.04]" : "text-gray-500 hover:bg-gray-100"}`}
                      >
                        + Optie toevoegen
                      </button>
                    </>
                  ) : (
                    <div className="space-y-2">
                      <p
                        className={`text-[11px] font-semibold uppercase ${isDark ? "text-gray-500" : "text-gray-400"}`}
                      >
                        Selecteer Categorie
                      </p>
                      <select
                        value={selectedCategory}
                        onChange={(e) => {
                          setSelectedCategory(e.target.value);
                          setNewFieldName(e.target.value);
                        }}
                        className={`w-full h-9 rounded-lg text-[13px] px-2 border ${isDark ? "bg-white/[0.04] border-white/[0.06] text-white" : "bg-white border-gray-200 text-gray-900"}`}
                      >
                        <option value="">Kies een categorie...</option>
                        {existingCategories.map((cat) => (
                          <option key={cat} value={cat}>
                            {cat}
                          </option>
                        ))}
                      </select>
                      <p
                        className={`text-[10px] ${isDark ? "text-gray-600" : "text-gray-400"}`}
                      >
                        De naam van het veld wordt: "{selectedCategory || "..."}
                        _select"
                      </p>
                    </div>
                  )}
                </div>
              )}
            </div>

            <div
              className={`px-4 sm:px-6 py-3 sm:py-4 border-t flex justify-end gap-3 shrink-0 ${isDark ? "border-white/[0.06]" : "border-gray-100"}`}
            >
              <button
                onClick={() => setAddFieldDialogOpen(false)}
                className={`h-9 px-4 rounded-lg text-[12px] font-medium border transition-colors ${isDark ? "border-white/[0.08] text-gray-400 hover:bg-white/[0.04]" : "border-gray-200 text-gray-600 hover:bg-gray-50"}`}
              >
                Annuleren
              </button>
              <button
                onClick={handleAddField}
                disabled={!newFieldName}
                className={`h-9 px-4 rounded-lg text-[12px] font-medium transition-colors disabled:opacity-40 ${isDark ? "bg-white text-black hover:bg-gray-200" : "bg-gray-900 text-white hover:bg-gray-800"}`}
              >
                Veld Toevoegen
              </button>
            </div>
          </NestedDialogContent>
        </NestedDialog>

        <NestedDialog
          open={deleteFieldDialogOpen}
          onOpenChange={setDeleteFieldDialogOpen}
        >
          <NestedDialogContent
            className={`sm:max-w-sm p-6 rounded-xl ${isDark ? "bg-[#141414] border border-white/[0.06]" : "bg-white"}`}
          >
            <NestedDialogHeader>
              <NestedDialogTitle className={isDark ? "text-white" : ""}>
                Veld verwijderen
              </NestedDialogTitle>
              <NestedDialogDescription
                className={isDark ? "text-gray-400" : ""}
              >
                Weet je zeker dat je dit veld wilt verwijderen? De data gaat
                verloren.
              </NestedDialogDescription>
            </NestedDialogHeader>
            <NestedDialogFooter className="mt-4">
              <button
                onClick={() => setDeleteFieldDialogOpen(false)}
                className={`h-9 px-4 rounded-lg text-[12px] font-medium border transition-colors ${isDark ? "border-white/[0.08] text-gray-400 hover:bg-white/[0.04]" : "border-gray-200 text-gray-600 hover:bg-gray-50"}`}
              >
                Annuleren
              </button>
              <button
                onClick={() => handleDeleteField(fieldToDelete)}
                className="h-9 px-4 rounded-lg text-[12px] font-medium bg-red-600 text-white hover:bg-red-700 transition-colors"
              >
                Verwijderen
              </button>
            </NestedDialogFooter>
          </NestedDialogContent>
        </NestedDialog>

        <NestedDialog
          open={linkSelectDialogOpen}
          onOpenChange={setLinkSelectDialogOpen}
        >
          <NestedDialogContent
            className={`sm:max-w-md p-6 rounded-xl ${isDark ? "bg-[#141414] border border-white/[0.06]" : "bg-white"}`}
          >
            <NestedDialogHeader>
              <NestedDialogTitle className={isDark ? "text-white" : ""}>
                Koppel aan categorie
              </NestedDialogTitle>
              <NestedDialogDescription
                className={isDark ? "text-gray-400" : ""}
              >
                Zet deze vaste keuzelijst om naar een gekoppelde lijst. De opties
                en gekozen waarden volgen dan automatisch de namen uit de gekozen
                categorie, ook na het hernoemen.
              </NestedDialogDescription>
            </NestedDialogHeader>
            <div className="py-3">
              <label
                className={`block text-[12px] font-medium mb-1.5 ${isDark ? "text-gray-400" : "text-gray-600"}`}
              >
                Categorie
              </label>
              <select
                value={linkSelectCategory}
                onChange={(e) => setLinkSelectCategory(e.target.value)}
                className={`w-full h-9 text-[13px] rounded-lg px-3 ${isDark ? "bg-white/[0.04] border border-white/[0.06] text-white" : "bg-white border border-gray-200 text-gray-900"}`}
              >
                <option value="">Selecteer categorie...</option>
                {existingCategories.map((cat) => (
                  <option key={cat} value={cat}>
                    {cat}
                  </option>
                ))}
              </select>
            </div>
            <NestedDialogFooter className="mt-2">
              <button
                onClick={() => setLinkSelectDialogOpen(false)}
                className={`h-9 px-4 rounded-lg text-[12px] font-medium border transition-colors ${isDark ? "border-white/[0.08] text-gray-400 hover:bg-white/[0.04]" : "border-gray-200 text-gray-600 hover:bg-gray-50"}`}
              >
                Annuleren
              </button>
              <button
                disabled={!linkSelectCategory}
                onClick={() => {
                  onConvertSelectToCategory(linkSelectField, linkSelectCategory);
                  setLinkSelectDialogOpen(false);
                }}
                className={`h-9 px-4 rounded-lg text-[12px] font-medium text-white transition-colors ${linkSelectCategory ? "bg-blue-600 hover:bg-blue-700" : "bg-blue-600/40 cursor-not-allowed"}`}
              >
                Koppelen
              </button>
            </NestedDialogFooter>
          </NestedDialogContent>
        </NestedDialog>

        <NestedDialog
          open={autoTranslateDialogOpen}
          onOpenChange={setAutoTranslateDialogOpen}
        >
          <NestedDialogContent
            className={`sm:max-w-md p-6 rounded-xl ${isDark ? "bg-[#141414] border border-white/[0.06]" : "bg-white"}`}
          >
            <NestedDialogHeader>
              <NestedDialogTitle className={isDark ? "text-white" : ""}>
                Automatisch Vertalen
              </NestedDialogTitle>
            </NestedDialogHeader>
            <div className="py-2">
              <p
                className={`text-[13px] mb-4 ${isDark ? "text-gray-400" : "text-gray-500"}`}
              >
                Selecteer de velden die je wilt vertalen. Huidige en lege
                vertalingen worden overschreven.
              </p>
              <div className="space-y-2 max-h-60 overflow-y-auto pr-2">
                {getTranslatableFields().map((fieldObj) => {
                  const isSelected = selectedFieldsForTranslation.includes(
                    fieldObj.field,
                  );
                  return (
                    <div
                      key={fieldObj.field}
                      className={`flex items-start gap-3 p-2 rounded-lg cursor-pointer transition-colors border ${
                        isSelected
                          ? isDark
                            ? "border-white/[0.15] bg-white/[0.06]"
                            : "border-gray-900 bg-gray-50"
                          : isDark
                            ? "border-transparent hover:bg-white/[0.02]"
                            : "border-transparent hover:bg-gray-50"
                      }`}
                      onClick={() => toggleFieldSelection(fieldObj.field)}
                    >
                      <div
                        className={`mt-0.5 w-4 h-4 rounded border flex items-center justify-center transition-colors ${
                          isSelected
                            ? isDark
                              ? "bg-white border-white"
                              : "bg-gray-900 border-gray-900"
                            : isDark
                              ? "border-white/[0.1] bg-transparent"
                              : "border-gray-300 bg-white"
                        }`}
                      >
                        {isSelected && (
                          <CheckSquare
                            className={`w-3 h-3 ${isDark ? "text-black" : "text-white"}`}
                          />
                        )}
                      </div>
                      <div className="flex-1">
                        <p
                          className={`text-[13px] font-medium capitalize leading-none mb-1 ${isDark ? "text-gray-200" : "text-gray-700"}`}
                        >
                          {fieldObj.displayName}
                        </p>
                        <p
                          className={`text-[12px] line-clamp-1 ${isDark ? "text-gray-600" : "text-gray-400"}`}
                        >
                          {fieldObj.preview}
                        </p>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
            <NestedDialogFooter>
              <button
                onClick={() => setAutoTranslateDialogOpen(false)}
                className={`h-9 px-4 rounded-lg text-[12px] font-medium transition-colors ${isDark ? "text-gray-400 hover:bg-white/[0.04]" : "text-gray-600 hover:bg-gray-50"}`}
              >
                Annuleren
              </button>
              <button
                onClick={handleAutoTranslate}
                disabled={
                  isTranslating || selectedFieldsForTranslation.length === 0
                }
                className={`h-9 px-4 rounded-lg text-[12px] font-medium flex items-center gap-2 transition-colors disabled:opacity-40 ${isDark ? "bg-white text-black hover:bg-gray-200" : "bg-gray-900 text-white hover:bg-gray-800"}`}
              >
                {isTranslating ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Sparkles className="w-4 h-4" />
                )}
                {isTranslating ? "Bezig met vertalen..." : "Start Vertaling"}
              </button>
            </NestedDialogFooter>
          </NestedDialogContent>
        </NestedDialog>

        <NestedDialog
          open={addLanguageDialogOpen}
          onOpenChange={setAddLanguageDialogOpen}
        >
          <NestedDialogContent
            className={`sm:max-w-sm p-6 rounded-xl ${isDark ? "bg-[#141414] border border-white/[0.06]" : "bg-white"}`}
          >
            <NestedDialogHeader>
              <NestedDialogTitle className={isDark ? "text-white" : ""}>
                Taal toevoegen
              </NestedDialogTitle>
            </NestedDialogHeader>
            <Input
              value={newLanguageCode}
              onChange={(e) => setNewLanguageCode(e.target.value.toLowerCase())}
              placeholder="Taalcode (bv. es, de)"
              maxLength={5}
              className={`my-4 h-9 text-[13px] rounded-lg ${isDark ? "bg-white/[0.04] border-white/[0.06] text-white placeholder-gray-600" : "bg-white border-gray-200 text-gray-900 placeholder-gray-400"}`}
              autoFocus
            />
            <NestedDialogFooter>
              <button
                onClick={() => setAddLanguageDialogOpen(false)}
                className={`h-9 px-4 rounded-lg text-[12px] font-medium transition-colors ${isDark ? "text-gray-400 hover:bg-white/[0.04]" : "text-gray-600 hover:bg-gray-50"}`}
              >
                Annuleren
              </button>
              <button
                onClick={handleAddLanguage}
                disabled={!newLanguageCode}
                className={`h-9 px-4 rounded-lg text-[12px] font-medium transition-colors disabled:opacity-40 ${isDark ? "bg-white text-black hover:bg-gray-200" : "bg-gray-900 text-white hover:bg-gray-800"}`}
              >
                Toevoegen
              </button>
            </NestedDialogFooter>
          </NestedDialogContent>
        </NestedDialog>
        {/* Mobile sticky save bar */}
        <div
          className={`sm:hidden flex items-center gap-2 px-3 py-2.5 border-t shrink-0 ${isDark ? "bg-[#141414] border-white/[0.06]" : "bg-white border-gray-100"}`}
        >
          <Button
            variant="outline"
            onClick={onCancel}
            className={`h-10 flex-1 rounded-lg text-[13px] font-medium ${isDark ? "border-white/[0.08] text-gray-400 hover:bg-white/[0.04]" : "border-gray-200 text-gray-600 hover:bg-gray-50"}`}
          >
            Annuleren
          </Button>
          <Button
            onClick={onSave}
            className={`h-10 flex-1 rounded-lg text-[13px] font-medium gap-2 ${isDark ? "bg-white text-black hover:bg-gray-200" : "bg-gray-900 text-white hover:bg-gray-800"}`}
          >
            <Save className="w-4 h-4" /> Opslaan
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ItemEditor;
